// Challenges API helpers
const resolveDefaultLlmUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const { protocol, host } = window.location;
    return `${protocol}//${host}/api/llm`;
  }
  try {
    if (typeof location !== 'undefined') {
      return `${location.protocol}//${location.host}/api/llm`;
    }
  } catch (_) {}
  return 'http://localhost:5001';
};

const LLM_URL = process.env.REACT_APP_LLM_URL || resolveDefaultLlmUrl();

async function authHeaders() {
  // Get Keycloak auth token (getToken function now returns Keycloak JWT)
  try {
    const { getToken } = await import('../firebase.js');
    const token = await getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function uploadChallenge(
  integrationId,
  imageFile,
  workgroupId,
  guidance = "",
  maxTurns = null,
  systemPrompt = null,
) {
  let base64Image = null;
  if (imageFile) {
    // Convert image file to base64
    base64Image = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]); // Remove data:image/...;base64, prefix
      reader.onerror = reject;
      reader.readAsDataURL(imageFile);
    });
  }

  const payload = {
    integration_id: integrationId,
    workgroup_id: workgroupId,
    ...(base64Image ? { screenshot_base64: base64Image } : {}),
    ...(guidance && guidance.trim().length > 0 ? { guidance: guidance.trim() } : {}),
    ...(Number.isInteger(maxTurns) && maxTurns > 0 ? { max_turns: maxTurns } : {}),
    ...(typeof systemPrompt === 'string' && systemPrompt.trim().length > 0
      ? { system_prompt: systemPrompt.trim() }
      : {}),
  };

  const res = await fetch(`${LLM_URL}/v1/challenges`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      ...(await authHeaders()) 
    },
    body: JSON.stringify(payload),
  });
  
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || `HTTP ${res.status}: ${res.statusText}`);
  }
  
  return res.json();
} 

export const getChallengeStatus = async (challengeId) => {
  try {
    const response = await fetch(`${LLM_URL}/v1/challenge_step/${challengeId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(await authHeaders())
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching challenge status:', error);
    throw error;
  }
};

export const getChallengeKnowledge = async (challengeId) => {
  try {
    const response = await fetch(`${LLM_URL}/v1/challenges/${challengeId}/knowledge`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(await authHeaders())
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching challenge knowledge:', error);
    throw error;
  }
};

export const getChallengeActivity = async (challengeId) => {
  try {
    console.log('Making API call to fetch activity for challenge:', challengeId);
    const response = await fetch(`${LLM_URL}/v1/challenge_activity/${challengeId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(await authHeaders())
      }
    });
    
    console.log('API response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('API response data:', data);
    return data;
  } catch (error) {
    console.error('Error fetching challenge activity:', error);
    throw error;
  }
}; 

export const getDisplayChallenges = async (integrationId) => {
  try {
    const response = await fetch(`${LLM_URL}/v1/integrations/${integrationId}/display_challenges`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(await authHeaders())
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Display challenges response:', data);
    return data;
  } catch (error) {
    console.error('Error fetching display challenges:', error);
    throw error;
  }
};

export const removeChunk = async (chunkId) => {
  try {
    const RAG_URL = process.env.REACT_APP_RAG_SERVER || 'http://172.16.100.179:9128/';
    const endpoint = `${RAG_URL}/api/remove-chunk`;
    const payload = { chunk_id: chunkId };
    
    console.log('🔍 removeChunk Debug Info:');
    console.log('  - RAG_URL:', RAG_URL);
    console.log('  - Full endpoint:', endpoint);
    console.log('  - Payload:', payload);
    console.log('  - Environment RAG_SERVER:', process.env.REACT_APP_RAG_SERVER);
    
    const headers = await authHeaders();
    console.log('  - Auth headers:', headers);
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      body: JSON.stringify(payload)
    });
    
    console.log('  - Response status:', response.status);
    console.log('  - Response headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('  - Error response body:', errorText);
      throw new Error(errorText || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('  - Success response:', result);
    return result;
  } catch (error) {
    console.error('❌ removeChunk Error Details:');
    console.error('  - Error type:', error.name);
    console.error('  - Error message:', error.message);
    console.error('  - Full error:', error);
    
    // Check if it's a CORS error specifically
    if (error.message.includes('CORS') || error.message.includes('cors')) {
      console.error('🚨 This appears to be a CORS error');
    }
    
    // Check if it's a network error
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('🌐 This appears to be a network/fetch error');
    }
    
    throw error;
  }
};

export const analyseChallenge = async (
  challengeId,
  message = '',
  conversation = [],
  context = {},
  integrationId,
) => {
  if (!challengeId) {
    throw new Error('challengeId is required for analysis');
  }

  const payload = {
    ...(message && message.trim().length > 0 ? { message: message.trim() } : {}),
    ...(Array.isArray(conversation) && conversation.length > 0
      ? {
          conversation: conversation.map((entry) => ({
            role: entry.role,
            content: entry.content,
          })),
        }
      : {}),
    ...(context && Object.keys(context).length > 0 ? { context } : {}),
    ...(integrationId ? { integration_id: integrationId } : {}),
  };

  const response = await fetch(`${LLM_URL}/v1/ChallangeAnalysis/${encodeURIComponent(challengeId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

export const analyseChallengeFollowup = async (
  sessionId,
  message,
  conversation = [],
  context = {},
  integrationId,
) => {
  if (!sessionId) {
    throw new Error('sessionId is required for follow-up analysis');
  }

  const normalizedSessionId = String(sessionId).trim();
  if (!normalizedSessionId) {
    throw new Error('sessionId is required for follow-up analysis');
  }

  const normalizedMessage = (() => {
    if (typeof message === 'string') {
      const trimmed = message.trim();
      if (!trimmed) return null;
      return { role: 'user', content: trimmed };
    }
    if (message && typeof message === 'object') {
      const role = typeof message.role === 'string' && message.role.trim() ? message.role.trim() : 'user';
      const content = typeof message.content === 'string' ? message.content.trim() : '';
      if (!content) return null;
      return { role, content };
    }
    return null;
  })();

  if (!normalizedMessage) {
    throw new Error('message is required for follow-up analysis');
  }

  const sanitizedConversation = Array.isArray(conversation)
    ? conversation
        .map((entry) => {
          const role = typeof entry?.role === 'string' && entry.role.trim() ? entry.role.trim() : null;
          const content = typeof entry?.content === 'string' ? entry.content.trim() : null;
          if (!role || !content) return null;
          return { role, content };
        })
        .filter(Boolean)
    : [];

  const payload = {
    message: normalizedMessage,
    session_id: normalizedSessionId,
    ...(sanitizedConversation.length > 0 ? { conversation: sanitizedConversation } : {}),
    ...(context && Object.keys(context).length > 0 ? { context } : {}),
    ...(integrationId ? { integration_id: integrationId } : {}),
  };

  const response = await fetch(`${LLM_URL}/v1/ChallangeAnalysis/Followup/${encodeURIComponent(normalizedSessionId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

// Delete a list of chunks by calling the remove-chunk endpoint for each chunk_id
// IMPORTANT: process deletions serially to avoid backend contention/race conditions
export const deleteChunks = async (chunkIds) => {
  try {
    const ids = Array.isArray(chunkIds) ? chunkIds.filter(Boolean) : [];
    console.log('[deleteChunks] Deleting chunk_ids (serial):', ids);

    if (!ids.length) return { ok: true, count: 0, deleted: [], failed: [] };

    const deleted = [];
    const failed = [];

    for (const id of ids) {
      try {
        const res = await removeChunk(id);
        if (res && typeof res === 'object' && 'success' in res) {
          if (res.success === true) {
            // Success response has keys: success, message
            deleted.push(id);
          } else {
            // Failure response has keys: success, error
            failed.push({ id, error: res?.error || 'remove-chunk reported success=false' });
          }
        } else {
          // Fallback: if shape unexpected, treat as failure
          failed.push({ id, error: 'Unexpected response from remove-chunk' });
        }
      } catch (err) {
        failed.push({ id, error: err?.message || String(err) });
      }
    }

    const ok = failed.length === 0;
    console.log('[deleteChunks] Summary (serial):', { ok, deletedCount: deleted.length, failedCount: failed.length });
    return { ok, count: ids.length, deleted, failed };
  } catch (err) {
    console.error('[deleteChunks] error:', err);
    return { ok: false, error: String(err) };
  }
};
